import { CopyToClipboardButton } from "portal/components/CopyToClipboardButton";
import { IconButton, Paper, Typography, useTheme } from "@mui/material";
import { PredictionPoint } from "protos/veselka/prediction_point";
import { RadioChipOption, RadioChips } from "portal/components/RadioChips";
import { ShowLabelsToggle } from "../../ShowLabelsToggle";
import { titleCase } from "common/utils/strings";
import { userFacingDate } from "portal/utils/categoryCollection/image";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import React, { FC, useMemo } from "react";

interface InfoTabProps {
  data: PredictionPoint;
  categoryOptions: RadioChipOption[];
  categoryId?: string;
  onClassify?: (predictionPointId: string, categoryId: string) => void;
}
export const InfoTab: FC<InfoTabProps> = ({
  data,
  categoryOptions,
  categoryId,
  onClassify,
}) => {
  const { t, i18n } = useTranslation();
  const { isInternal } = useSelf();

  const { id, x, y, radius, image } = data;
  const capturedAt = image?.capturedAt
    ? userFacingDate(i18n, image.capturedAt)
    : undefined;
  const externalInfo: MetadataProps[] = [
    {
      label: t("models.categoryCollectionProfiles.metadata.capturedAt"),
      value: capturedAt,
    },
  ];
  const internalInfo: MetadataProps[] = [
    {
      label: t("models.categoryCollectionProfiles.metadata.pointId"),
      value: id,
      type: "id",
    },
    {
      label: t("models.categoryCollectionProfiles.metadata.imageId"),
      value: image?.id,
      type: "id",
    },
    { label: t("models.categoryCollectionProfiles.metadata.x"), value: x },
    { label: t("models.categoryCollectionProfiles.metadata.y"), value: y },
    {
      label: t("models.categoryCollectionProfiles.metadata.radius"),
      value: radius,
    },
    {
      label: t("models.categoryCollectionProfiles.metadata.ppcm"),
      value: image?.ppcm,
    },
  ];

  const clipBoardData = useMemo(
    () => JSON.stringify(PredictionPoint.toJSON(data)),
    [data]
  );

  const activeCategory = useMemo(
    () => categoryOptions.find((o) => o.id === categoryId),
    [categoryId, categoryOptions]
  );

  return (
    <div className="text-xs md:text-sm pb-2 px-2 lg:pb-4 lg:px-4 flex flex-col gap-2 lg:gap-4 overflow-y-auto">
      <ShowLabelsToggle size="small" />
      <div>
        <Typography variant="caption">
          {titleCase(t("models.categoryCollectionProfiles.category_one"))}
        </Typography>
        <div className="flex gap-1">
          {onClassify ? (
            <RadioChips
              isLoading={false}
              options={categoryOptions}
              activeId={categoryId}
              setActiveId={(categoryId) => {
                onClassify(data.id, categoryId);
              }}
              size="small"
              forceDropdown
            />
          ) : (
            <Typography
              className="px-3 rounded-full text-sm"
              style={{
                backgroundColor: activeCategory?.color?.bg,
                color: activeCategory?.color?.text,
              }}
            >
              {activeCategory?.name ?? t("utils.descriptors.unknown")}
            </Typography>
          )}
          {onClassify && (
            <IconButton
              color="inherit"
              disabled={!categoryId}
              onClick={() => {
                if (!categoryId) {
                  return;
                }
                onClassify(data.id, categoryId);
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </div>
      <div>
        {externalInfo.map((info) => (
          <Metadata key={info.label} {...info} />
        ))}
      </div>
      {isInternal && (
        <div>
          <div className="flex gap-2 justify-between">
            <Typography className="text-base font-bold">
              {t("models.categoryCollectionProfiles.metadata.internal")}
            </Typography>
            <CopyToClipboardButton text={clipBoardData} />
          </div>
          <Paper variant="section" className="p-1">
            {internalInfo.map((info) => (
              <Metadata key={info.label} {...info} />
            ))}
          </Paper>
        </div>
      )}
    </div>
  );
};

interface MetadataProps {
  label: string;
  value?: string | number;
  type?: "text" | "id";
}
const Metadata: FC<MetadataProps> = ({ label, value, type = "text" }) => {
  const muiTheme = useTheme();
  const isId = type === "id";
  return (
    <p className="m-0 p-0">
      <span className="pr-2">{label}</span>
      <span
        className={isId ? "font-mono" : undefined}
        style={{
          color: isId ? muiTheme.palette.warning.main : undefined,
        }}
      >
        {value}
      </span>
    </p>
  );
};
